import { useCallback, useRef } from "react";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useScrollToPageTopOnMount } from "@tudigo-monorepo/core-tudigo-utils";
import { useGetWebflowInvestorTestimonials } from "@tudigo-monorepo/web-tudigo-api-client";

import { ProjectsCardGrid } from "../../../shared-external/components/projects-card-grid";
import { SearchProjectsPagination } from "../../shared-internal/components/search-projects/search-projects-pagination";
import { useFinishedProjectsLayout } from "../hooks/use-finished-projects-layout";
import { InvestorTestimonialsSlider } from "./investor-testimonials-slider/investor-testimonials-slider";
import { InvestorTestimonialsSliderSkeleton } from "./investor-testimonials-slider/investor-testimonials-slider.skeleton";

export function DefaultFinishedProjectsLayout() {
  const projects = useFinishedProjectsLayout();
  const {
    data: testimonials,
    isFetching: isFetchingTestimonials,
    isFetched: isTestimonialsFetched,
  } = useGetWebflowInvestorTestimonials();

  const sliderContainerRef = useRef<HTMLDivElement | null>(null);

  const scrollToCardsTop = useCallback(() => {
    if (!sliderContainerRef.current) {
      window.scrollTo({ top: 0, behavior: "smooth" });

      return;
    }

    const CAROUSEL_CONTAINER_MB = 48;
    const { offsetTop, offsetHeight } = sliderContainerRef.current;
    const position = offsetTop + offsetHeight - CAROUSEL_CONTAINER_MB;

    window.scrollTo({ top: position, behavior: "smooth" });
  }, []);

  const setSliderContainerRef = useCallback((element: HTMLDivElement) => {
    sliderContainerRef.current = element;
  }, []);

  const { scroller } = useScrollToPageTopOnMount(setSliderContainerRef);

  if (isTestimonialsFetched && testimonials?.data?.items.length === 0) {
    return <ProjectsCardGrid projects={projects} />;
  }

  return (
    <>
      <section
        ref={scroller}
        className={cn(
          "bg-accent-super-light border-accent-light font-roobert flex flex-col items-center justify-center gap-y-10 border text-center",
          "w-screen sm:w-full",
          "-ml-4 mb-12 mt-6 sm:ml-0",
          "px-4 py-6 md:p-10",
          "sm:rounded-lg",
        )}
      >
        {isFetchingTestimonials ? (
          <InvestorTestimonialsSliderSkeleton />
        ) : (
          <InvestorTestimonialsSlider />
        )}
      </section>

      <ProjectsCardGrid projects={projects} />

      <SearchProjectsPagination onPageChange={scrollToCardsTop} />
    </>
  );
}
